import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import HollowButton from "components/HollowButton";
import { Option } from "components/OptionsCard/typings";
import OutgoingPrintModal from "components/OutgoingPrintModal";
import officialLettersService from "api/officialLettersService";
import {
  EDocumentInternalStatus,
  ILetterProject,
} from "dto/OfficialLetters/IOfficialLetters";
import { IOutgoingPrintSource } from "dto/Printing/IOutgoingPrint";
import ParcelPreviewModal from "modules/biuroPodawczeModule/ParcelPreviewModal";

import LetterRowMenu from "./LetterRowMenu";
import useLetterActions from "./useLetterActions";
import styles from "./officialLetters.module.scss";

interface Props {
  searchText: string;
}

type StatusFilter = "all" | "draft" | "toSign" | "passed";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Wszystkie" },
  { key: "draft", label: "Projekt" },
  { key: "toSign", label: "Podpis własny" },
  { key: "passed", label: "Przekazane do podpisu" },
];

const matchesFilter = (p: ILetterProject, f: StatusFilter) => {
  switch (f) {
    case "draft":
      return p.InternalStatus === EDocumentInternalStatus.Project || p.InternalStatus === EDocumentInternalStatus.GeneratedLetters;
    case "toSign":
      return p.InternalStatus === EDocumentInternalStatus.ReadyToSign;
    case "passed":
      return p.InternalStatus === EDocumentInternalStatus.ReadyToDigitalSign;
    default:
      return true;
  }
};

const statusClass = (s: EDocumentInternalStatus) => {
  switch (s) {
    case EDocumentInternalStatus.ReadyToSign:
      return styles.badgeWarn;
    case EDocumentInternalStatus.ReadyToDigitalSign:
      return styles.badgeInfo;
    default:
      return styles.badgeGray;
  }
};

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString("pl-PL") : "—");

// Edycja tylko projektu bez wygenerowanych pism — pismo z pismami do podpisu poprawia się
// przez „Popraw" (usuwa pisma), żeby treść i wygenerowane kopie się nie rozjechały.
const canEditProject = (p: ILetterProject) =>
  p.InternalStatus !== EDocumentInternalStatus.ReadyToDigitalSign && p.ParcelsCount === 0;

const matchesSearch = (p: ILetterProject, q: string) => {
  const has = (v?: string | null) => !!v && v.toLowerCase().includes(q);
  return (
    has(p.Title) ||
    has(p.CaseSignature) ||
    has(p.Receivers) ||
    has(p.MajorSignature) ||
    has(p.PendingSignerName) ||
    fmtDate(p.LastModified ?? p.CreationDate).includes(q)
  );
};

// „Projekty pism" — port Documents.List.NormalProjectsList z Magistratu. Pisma autora do
// momentu przekazania do wysyłki: redakcja, podpis tradycyjny (wygenerowane pisma czekają na
// wydruk, podpis odręczny i „Przekaż do wysyłki"), przekazane do podpisu elektronicznego.
// Po przekazaniu do wysyłki pismo znika stąd i pojawia się w „Wychodzących pismach".
const LetterProjectsList = ({ searchText }: Props) => {
  const navigate = useNavigate();
  const { data, isLoading } = officialLettersService.useGetProjects();
  const { mutate: setReady } = officialLettersService.useSetReadyToDispatch();
  const { mutate: returnToProject } = officialLettersService.useReturnToProject();
  const { mutate: approve } = officialLettersService.useApprove();
  const { openInEditor, generateLetters, run, confirmModal } = useLetterActions();

  const [filter, setFilter] = useState<StatusFilter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ parcelId: string; title?: string | null } | null>(null);
  const [printing, setPrinting] = useState<{ source: IOutgoingPrintSource; title: string } | null>(null);

  const q = searchText.trim().toLowerCase();
  const items = useMemo(
    () =>
      (data?.data?.Items ?? []).filter(
        (p) => matchesFilter(p, filter) && (q.length === 0 || matchesSearch(p, q)),
      ),
    [data, filter, q],
  );

  useEffect(() => setSelected(new Set()), [filter, q]);

  const selectedItems = items.filter((p) => selected.has(p.DocumentId));
  const readyToRelease = selectedItems.filter(
    (p) => p.InternalStatus === EDocumentInternalStatus.ReadyToSign && p.ParcelsCount > 0,
  );

  const toggleIn = (setter: typeof setSelected, id: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const release = (docs: ILetterProject[]) =>
    run(
      setReady,
      docs.map((d) => d.DocumentId),
      "Przekazano do wysyłki",
      {
        title: "Przekaż do wysyłki",
        text:
          docs.length > 1
            ? `Czy przekazać do wysyłki wybrane pisma (${docs.length})? Trafią do biura podawczego.`
            : "Czy przekazać pismo do wysyłki? Wygenerowane pisma trafią do biura podawczego.",
        acceptLabel: "Przekaż",
      },
      () => setSelected(new Set()),
    );

  const rowOptions = (p: ILetterProject): Option[] => {
    const isDraft =
      p.InternalStatus === EDocumentInternalStatus.Project ||
      p.InternalStatus === EDocumentInternalStatus.GeneratedLetters;
    const isToSign = p.InternalStatus === EDocumentInternalStatus.ReadyToSign;
    const isPassed = p.InternalStatus === EDocumentInternalStatus.ReadyToDigitalSign;
    return [
      { name: "Edytuj w edytorze", callback: () => openInEditor(p), additional: false, isEnabled: canEditProject(p), canMultiple: false },
      {
        name: isToSign ? "Generuj pisma ponownie" : "Generuj pisma / podpisz",
        callback: () => generateLetters(p),
        additional: false,
        isEnabled: !isPassed,
        canMultiple: false,
      },
      {
        // Magistrat: DocumentListBaseViewModel.PrintCommand — pismo z wygenerowanymi pismami
        // drukuje się kolejką adresatów, projekt bez pism — sam dokument.
        name: "Drukuj…",
        callback: () => setPrinting({ source: { DocumentIds: [p.DocumentId] }, title: p.Title || "Pismo bez tytułu" }),
        additional: false,
        isEnabled: true,
        canMultiple: false,
      },
      {
        name: "Podgląd pism",
        callback: () => {
          if (p.Parcels.length === 1) setPreview({ parcelId: p.Parcels[0].Id, title: p.Title });
          else setExpanded((prev) => new Set(prev).add(p.DocumentId));
        },
        additional: false,
        isEnabled: p.Parcels.length > 0,
        canMultiple: false,
      },
      {
        name: "Przekaż do wysyłki",
        callback: () => release([p]),
        additional: false,
        isEnabled: isToSign && p.ParcelsCount > 0,
        canMultiple: false,
        splitterBefore: true,
      },
      {
        name: "Popraw",
        callback: () =>
          run(
            returnToProject,
            [p.DocumentId],
            "Cofnięto do projektu",
            {
              title: "Popraw pismo",
              text: "Wygenerowane pisma zostaną usunięte, a pismo wróci do statusu „Projekt”, gdzie można je poprawić i wygenerować ponownie. Kontynuować?",
              acceptLabel: "Popraw",
            },
          ),
        additional: false,
        isEnabled: isToSign,
        canMultiple: false,
      },
      {
        name: "Zatwierdź bez wysyłki",
        callback: () =>
          run(
            approve,
            [p.DocumentId],
            "Zatwierdzono",
            {
              title: "Zatwierdź bez wysyłki",
              text: "Po zatwierdzeniu pismo zniknie z listy projektów i będzie dostępne tylko z poziomu teczki. Zatwierdzić?",
              acceptLabel: "Zatwierdź",
            },
          ),
        additional: false,
        isEnabled: isDraft && !!p.CaseId && p.ParcelsCount === 0,
        canMultiple: false,
      },
      {
        name: "Idź do teczki",
        callback: () => navigate(`/cases/${p.CaseId}/dossier`),
        additional: false,
        isEnabled: !!p.CaseId,
        canMultiple: false,
        splitterBefore: true,
      },
    ];
  };

  const renderRow = (p: ILetterProject) => {
    const open = expanded.has(p.DocumentId);
    const isPassed = p.InternalStatus === EDocumentInternalStatus.ReadyToDigitalSign;
    const canEdit = canEditProject(p);
    return (
      <div key={p.DocumentId} className={styles.card}>
        <div
          className={styles.cardHead}
          title={canEdit ? "Dwuklik — otwórz w edytorze" : undefined}
          onDoubleClick={() => canEdit && openInEditor(p)}
        >
          <input
            type="checkbox"
            checked={selected.has(p.DocumentId)}
            onChange={() => toggleIn(setSelected, p.DocumentId)}
            onClick={(e) => e.stopPropagation()}
          />
          {p.Parcels.length > 0 ? (
            <button
              type="button"
              className={styles.expandBtn}
              onClick={() => toggleIn(setExpanded, p.DocumentId)}
              title={open ? "Zwiń" : "Pokaż wygenerowane pisma"}
            >
              <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
            </button>
          ) : (
            <span className={styles.expandPlaceholder} />
          )}
          <div className={styles.cardMain}>
            <div className={styles.cardTitle}>
              {p.Title || "Bez tytułu"}
              <span className={`${styles.badge} ${statusClass(p.InternalStatus)}`}>
                {p.InternalStatusName}
                {isPassed && p.PendingSignerName ? ` · ${p.PendingSignerName}` : ""}
              </span>
              {p.MajorSignature && <span className={styles.sig}>{p.MajorSignature}</span>}
              {p.HasSignedVersion && !isPassed && <span className={`${styles.badge} ${styles.badgeOk}`}>podpisane</span>}
            </div>
            <div className={styles.cardSub}>
              {p.Receivers ?? (p.ParcelsCount > 0 ? "" : "brak wygenerowanych pism")}
              {p.CaseSignature ? ` · teczka ${p.CaseSignature}` : " · bez teczki"}
            </div>
          </div>
          <div className={styles.cardMeta}>
            <div>{fmtDate(p.LastModified ?? p.CreationDate)}</div>
            {p.ParcelsCount > 0 && <div>pism: {p.ParcelsCount}</div>}
          </div>
          {p.InternalStatus === EDocumentInternalStatus.ReadyToSign && p.ParcelsCount > 0 && (
            <HollowButton text="Przekaż do wysyłki" onClick={() => release([p])} />
          )}
          <LetterRowMenu options={rowOptions(p)} />
        </div>

        {open && p.Parcels.length > 0 && (
          <div className={styles.parcels}>
            {p.Parcels.map((x) => (
              <div
                key={x.Id}
                className={styles.parcel}
                title="Dwuklik — podgląd / wydruk pisma"
                onDoubleClick={() => setPreview({ parcelId: x.Id, title: `${p.Title ?? ""} — ${x.ReceiverName ?? ""}` })}
              >
                <div className={styles.parcelMain}>
                  <div className={styles.parcelTitle}>
                    {x.ReceiverName ?? "—"}
                    {x.ParcelNumber && <span className={styles.sig}>{x.ParcelNumber}</span>}
                  </div>
                  <div className={styles.parcelSub}>{x.DeliveryKindName ?? "brak rodzaju przesyłki"}</div>
                </div>
                <LetterRowMenu
                  options={[
                    {
                      name: "Drukuj…",
                      callback: () =>
                        setPrinting({ source: { ParcelIds: [x.Id] }, title: `${p.Title ?? ""} — ${x.ReceiverName ?? ""}` }),
                      additional: false,
                      isEnabled: true,
                      canMultiple: false,
                    },
                    {
                      name: "Podgląd pisma",
                      callback: () => setPreview({ parcelId: x.Id, title: `${p.Title ?? ""} — ${x.ReceiverName ?? ""}` }),
                      additional: false,
                      isEnabled: true,
                      canMultiple: false,
                    },
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const total = data?.data?.Items?.length ?? 0;

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.filters}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.filterChip} ${filter === f.key ? styles.filterChipActive : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className={styles.topActions}>
          <span className={styles.topInfo}>
            {selected.size > 0 ? `Zaznaczono: ${selected.size}` : `Projektów: ${items.length}${items.length !== total ? ` z ${total}` : ""}`}
          </span>
          {selectedItems.length > 0 && (
            <HollowButton
              text={`Drukuj (${selectedItems.length})`}
              onClick={() =>
                setPrinting({
                  source: { DocumentIds: selectedItems.map((p) => p.DocumentId) },
                  title: `Zaznaczone pisma (${selectedItems.length})`,
                })
              }
            />
          )}
          {readyToRelease.length > 0 && (
            <HollowButton text={`Przekaż do wysyłki (${readyToRelease.length})`} onClick={() => release(readyToRelease)} />
          )}
        </div>
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.empty}>Ładowanie…</div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            {total === 0
              ? "Brak projektów pism. Pisma tworzone w edytorze pojawiają się tu do momentu przekazania do wysyłki."
              : "Brak pism spełniających kryteria."}
          </div>
        ) : (
          <div className={styles.cards}>{items.map(renderRow)}</div>
        )}
      </div>

      {preview && <ParcelPreviewModal parcelId={preview.parcelId} title={preview.title} onClose={() => setPreview(null)} />}
      {printing && (
        <OutgoingPrintModal
          source={printing.source}
          context="projects"
          title={printing.title}
          onClose={() => setPrinting(null)}
        />
      )}
      {confirmModal}
    </>
  );
};

export default LetterProjectsList;
